import React from 'react';

import * as api from 'config/api';
import * as consts from 'config/consts';
import { BrokerPositionItem } from './SymbolPositionItem';
import { InvestmentTrustPositionItem } from './InvestmentTrustPositionItem';

type SymbolPositionListProps = {
  symbol: string;
  positions: Array<api.GetPositionsResultItem>;
};

const toBrokerName = (brokerId: string): JSX.Element => {
  const name = ((): string => {
    if (consts.BrokerIDss.SBI証券.includes(brokerId)) return 'SBI証券';
    if (consts.BrokerIDss.三菱UFJeスマート証券.includes(brokerId))
      return '三菱UFJ eスマート証券';
    if (consts.BrokerIDss.みんかぶロボ?.includes(brokerId))
      return 'みんかぶロボ';
    if (consts.BrokerIDss.松井証券.includes(brokerId)) return '松井証券';
    if (consts.BrokerIDss.楽天証券.includes(brokerId)) return '楽天証券';
    if (consts.BrokerIDss.マネックス証券?.includes(brokerId))
      return 'マネックス証券';
    if (consts.BrokerIDss.野村證券?.includes(brokerId)) return '野村證券';
    if (consts.BrokerIDss.大和証券?.includes(brokerId)) return '大和証券';
    if (consts.BrokerIDss.SMBC日興証券?.includes(brokerId))
      return 'SMBC日興証券';
    if (consts.BrokerIDss.岡三証券?.includes(brokerId)) return '岡三証券';
    if (consts.BrokerIDss.みずほ証券?.includes(brokerId)) return 'みずほ証券';
    if (consts.BrokerIDss.三菱UFJモルガンスタンレー証券?.includes(brokerId))
      return '三菱UFJモルガンスタンレー証券';
    if (consts.BrokerIDss['JIS&T']?.includes(brokerId)) return 'JIS&T';
    if (consts.BrokerIDss.NRK?.includes(brokerId)) return 'NRK';
    if (consts.BrokerIDss.SBIベネフィットシステムズ?.includes(brokerId))
      return 'SBIベネフィットシステムズ';
    return '';
  })();
  const suffix = ((): string => {
    if (consts.brokerIDsToArray(consts.TecotecBrokerIds).includes(brokerId)) {
      return 'みんかぶ口座連携(旧)';
    }
    if (consts.brokerIDsToArray(consts.MONOBrokerIds).includes(brokerId)) {
      return 'みんかぶ口座連携';
    }
    if (consts.brokerIDsToArray(consts.MoneytreeBrokerIds).includes(brokerId)) {
      return 'マネーツリー連携';
    }
    return '';
  })();
  return (
    <>
      <div className="font-bold">{name}</div>
      <div className="ml-auto text-xs">{suffix}</div>
    </>
  );
};

const groupByBrokerID = (
  positions: SymbolPositionListProps['positions']
): {
  brokerId: string;
  items: {
    brokerId: string;
    productType: number;
    profitOrLoss: number | null;
    quantity: number | null;
    valuation: number | null;
    cashMargin: number;
    side: number;
  }[];
}[] => {
  const ids: string[] = [];
  // eslint-disable-next-line array-callback-return
  positions.map((position, i) => {
    if (!ids.includes(position.BrokerID)) ids[i] = position.BrokerID;
  });

  const result = ids.map(id => {
    const list = positions.filter(position => position.BrokerID === id);
    return {
      brokerId: id,
      items: list.map(item => {
        return {
          brokerId: item.BrokerID,
          productType: item.ProductType,
          profitOrLoss: item.ProfitOrLoss,
          quantity: item.Qty,
          valuation: item.CurrentAmount,
          cashMargin: item.CashMargin,
          side: item.Side,
        };
      }, []),
    };
  });
  return result;
};

const sumGrouped = (
  item: ReturnType<typeof groupByBrokerID>[number]
): {
  brokerId: string;
  productType: number;
  profitOrLoss: number | null;
  quantity: number | null;
  valuation: number | null;
} =>
  item.items.reduce(
    (acc, v): Omit<typeof v, 'side' | 'cashMargin'> => {
      const isProfitOrLossAllNull = item.items.every(
        item => item.profitOrLoss === null
      );

      const summryProfitOrLoss = isProfitOrLossAllNull
        ? null
        : item.items.reduce((acc, v) => {
            return (acc ?? 0) + (v.profitOrLoss ?? 0);
          }, 0);

      const isQuantityAllNull = item.items.every(
        item => item.quantity === null
      );
      const summryQuantity = isQuantityAllNull
        ? null
        : item.items.reduce((acc, v) => {
            return (acc ?? 0) + (v.quantity ?? 0);
          }, 0);

      const isValuationAllNull = item.items.every(
        item => item.valuation === null
      );
      const summryValuation = isValuationAllNull
        ? null
        : item.items.reduce((acc, v) => {
            const valuationValue =
              v.cashMargin === consts.CashMargin.信用 ? 0 : v.valuation ?? 0;
            return (acc ?? 0) + valuationValue;
          }, 0);

      return {
        brokerId: item.items[0].brokerId,
        productType: item.items[0].productType,
        profitOrLoss: summryProfitOrLoss,
        quantity: summryQuantity,
        valuation: summryValuation,
      };
    },
    {
      brokerId: '',
      productType: 0,
      profitOrLoss: 0,
      quantity: 0,
      valuation: 0,
    } as Omit<typeof item.items[number], 'side' | 'cashMargin'>
  );

const SymbolPositionList: React.FC<SymbolPositionListProps> = ({
  symbol,
  positions,
}) => {
  const groupedByBrokerIDPositions = React.useMemo(
    () => groupByBrokerID(positions),
    [positions]
  );
  return (
    <div id="id_symbolPositionList">
      {positions.every(
        item => item.ProductType === consts.productTypes.投資信託
      )
        ? groupedByBrokerIDPositions.map((item, index) => {
            const summed = sumGrouped(item);
            return (
              <InvestmentTrustPositionItem
                {...{
                  brokerName: toBrokerName(summed.brokerId),
                  profitOrLoss: summed.profitOrLoss,
                  quantity: summed.quantity,
                  valuation: summed.valuation,
                  key: index,
                }}
              />
            );
          })
        : groupedByBrokerIDPositions.map((item, key) => {
            const summed = sumGrouped(item);
            return (
              <BrokerPositionItem
                key={key}
                {...{
                  brokerName: toBrokerName(item.brokerId),
                  items: item.items.map(item => {
                    return {
                      profitOrLoss: item.profitOrLoss,
                      productType: item.productType,
                      quantity: item.quantity,
                      valuation: item.valuation,
                      cashMargin: item.cashMargin,
                      side: item.side,
                    };
                  }),
                  sum: summed,
                }}
              />
            );
          })}
    </div>
  );
};

export default SymbolPositionList;
